import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  ListGroup,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { AsyncPaginate } from "react-select-async-paginate";
import { authFetch } from "../utils/authFetch";
import JurnalTvaIesiriIncasare from "./JurnalTvaIesiriIncasare";
import JurnalTvaIesiriValuta from "./JurnalTvaIesiriValuta";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";

const LIMIT = 100;

export default function RapoarteIesiri() {
  const navigate = useNavigate();
  const [activeReport, setActiveReport] = useState("clienti_neincasati");

  // Date filters
  const today = new Date();
  const yyyy = today.getFullYear();

  const [from, setFrom] = useState(new Date(yyyy, today.getMonth(), 1));
  const [to, setTo] = useState(today);
  const [atDate, setAtDate] = useState(today);

  const [contact, setContact] = useState(null);
  const [partFilter, setPartFilter] = useState(null);

  // States for fetching
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ rows: [], totals_by_cota: [] });
  const [loaded, setLoaded] = useState(false);

  // Accordion state for clients invoices list
  const [expandedClients, setExpandedClients] = useState({});

  const fmt2 = (n) =>
    Number(n ?? 0).toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Load options for contact picker
  const loadContactOptions = useCallback(async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const skip = (page - 1) * LIMIT;
    try {
      const res = await authFetch(
        `/contacte/?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`
      );
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.items)
          ? res.items
          : [];
      const total =
        typeof res?.total === "number"
          ? res.total
          : typeof res?.count === "number"
            ? res.count
            : undefined;

      const options = items.map((c) => ({
        value: c.id,
        label: `${c.bxdenumire}${c.bxcodfiscal ? ` (${c.bxcodfiscal})` : ""}`,
      }));

      const hasMore =
        typeof total === "number"
          ? skip + items.length < total
          : items.length === LIMIT;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  }, []);

  const loadPartOptions = useCallback(async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const skip = (page - 1) * LIMIT;
    try {
      const res = await authFetch(
        `/bxpart/?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`
      );
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.items)
          ? res.items
          : [];
      const total =
        typeof res?.total === "number"
          ? res.total
          : typeof res?.count === "number"
            ? res.count
            : undefined;

      const options = items.map((p) => ({
        value: p.id,
        label: `${p.cod || ""} — ${p.denumire || ""}`.trim(),
      }));

      const hasMore =
        typeof total === "number"
          ? skip + items.length < total
          : items.length === LIMIT;

      return {
        options,
        hasMore,
        additional: { page: page + 1 },
      };
    } catch (e) {
      console.error(e);
      return { options: [], hasMore: false, additional: { page } };
    }
  }, []);

  const formatDateString = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const loadData = async () => {
    setBusy(true);
    setErr("");
    setLoaded(false);
    setData({ rows: [], totals_by_cota: [] });
    try {
      let url = "";
      const contactId = contact?.value;
      const contactQuery = contactId ? `&contact_id=${contactId}` : "";
      
      const partId = partFilter?.value;
      const partQuery = partId ? `&part_id=${partId}` : "";
      
      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const atDateStr = formatDateString(atDate);

      if (activeReport === "clienti_neincasati") {
        url = `/rapoarte/clienti-neincasati?at_date=${atDateStr}${contactQuery}`;
      } else if (activeReport === "iesiri_perioada") {
        url = `/rapoarte/iesiri-perioada?from=${fromStr}&to=${toStr}${contactQuery}`;
      } else if (activeReport === "tva_direct") {
        url = `/rapoarte/jurnal-tva-iesiri-direct?from=${fromStr}&to=${toStr}`;
      } else if (activeReport === "vanzari_trasabilitate") {
        url = `/rapoarte/vanzari-trasabilitate?from=${fromStr}&to=${toStr}${contactQuery}${partQuery}`;
      } else if (activeReport === "vanzari_produse_finite_reteta") {
        url = `/rapoarte/vanzari-produse-finite-reteta?from=${fromStr}&to=${toStr}${contactQuery}${partQuery}`;
      }

      if (url) {
        const res = await authFetch(url);
        if (activeReport === "clienti_neincasati" || activeReport === "iesiri_perioada") {
          setData({ rows: Array.isArray(res?.rows) ? res.rows : [], totals_by_cota: [] });
        } else if (activeReport === "vanzari_trasabilitate" || activeReport === "vanzari_produse_finite_reteta") {
          setData({ rows: Array.isArray(res) ? res : [], totals_by_cota: [] });
        } else {
          setData({
            rows: Array.isArray(res?.rows) ? res.rows : [],
            totals_by_cota: Array.isArray(res?.totals_by_cota) ? res.totals_by_cota : [],
          });
        }
        setLoaded(true);
      }
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare date raport.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setExpandedClients({});
    setData({ rows: [], totals_by_cota: [] });
    setLoaded(false);
    setErr("");
    setContact(null);
    setPartFilter(null);
  }, [activeReport]);

  const groupedClients = useMemo(() => {
    if (activeReport !== "clienti_neincasati") return [];
    const map = {};
    for (const r of data.rows || []) {
      const kid = r.contact_id;
      if (!map[kid]) {
        map[kid] = {
          contact_id: r.contact_id,
          contact_name: r.contact_name,
          total_value: 0,
          total_settled: 0,
          total_sold: 0,
          invoices: [],
        };
      }
      map[kid].total_value += r.bxgvalue;
      map[kid].total_settled += r.settled_value;
      map[kid].total_sold += r.bxsold;
      map[kid].invoices.push(r);
    }
    return Object.values(map).sort((a, b) => b.total_sold - a.total_sold);
  }, [data.rows, activeReport]);

  const toggleClient = (id) => {
    setExpandedClients((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Grouped VAT invoices for tva_direct report
  const groupedTvaDirect = useMemo(() => {
    if (activeReport !== "tva_direct") return [];
    const m = new Map();
    for (const r of data.rows || []) {
      const k = r.doc_id;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    const arr = Array.from(m.entries()).map(([doc_id, rows]) => {
      rows.sort((a, b) => Number(a.cota ?? 0) - Number(b.cota ?? 0));
      return { doc_id, rows };
    });

    arr.sort((A, B) => {
      const a0 = A.rows[0] || {};
      const b0 = B.rows[0] || {};
      const da = String(a0.doc_date || "");
      const db = String(b0.doc_date || "");
      if (da !== db) return da.localeCompare(db);
      return String(a0.doc_no || "").localeCompare(String(b0.doc_no || ""));
    });
    return arr;
  }, [data.rows, activeReport]);

  const totalsGeneralDirect = useMemo(() => {
    const t = data.totals_by_cota || [];
    return {
      baza: t.reduce((s, x) => s + Number(x.baza || 0), 0),
      tva: t.reduce((s, x) => s + Number(x.tva || 0), 0),
      total: t.reduce((s, x) => s + Number(x.total || 0), 0),
    };
  }, [data.totals_by_cota]);

  const groupedFinishedProducts = useMemo(() => {
    if (activeReport !== "vanzari_produse_finite_reteta") return [];
    const map = {};
    for (const r of data.rows || []) {
      const key = `${r.factura}-${r.sale_line_id}`;
      if (!map[key]) {
        map[key] = {
          factura: r.factura,
          data_vanzarii: r.data_vanzarii,
          client: r.client,
          finit_cod: r.finit_cod,
          finit_denumire: r.finit_denumire,
          finit_cantitate: r.finit_cantitate,
          ingredients: [],
        };
      }
      map[key].ingredients.push(r);
    }
    return Object.values(map);
  }, [data.rows, activeReport]);

  return (
    <div className="p-3">
      <Row className="g-4">
        {/* Left menu column */}
        <Col xs={12} md={3} className="d-print-none">
          <Card className="shadow-sm border-0 rounded-4 overflow-hidden mb-3">
            <Card.Header className="bg-primary text-white py-3">
              <h5 className="fw-bold mb-0">Rapoarte Ieșiri</h5>
            </Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item
                action
                active={activeReport === "clienti_neincasati"}
                onClick={() => setActiveReport("clienti_neincasati")}
                className="py-3 px-4 border-0"
              >
                Clienți neîncasați la dată
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "iesiri_perioada"}
                onClick={() => setActiveReport("iesiri_perioada")}
                className="py-3 px-4 border-0"
              >
                Ieșiri în perioadă
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "tva_direct"}
                onClick={() => setActiveReport("tva_direct")}
                className="py-3 px-4 border-0"
              >
                Jurnal TVA direct taxabile
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "tva_incasare"}
                onClick={() => setActiveReport("tva_incasare")}
                className="py-3 px-4 border-0"
              >
                Jurnal TVA la încasare
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "tva_valuta"}
                onClick={() => setActiveReport("tva_valuta")}
                className="py-3 px-4 border-0"
              >
                Jurnal TVA în valută
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "vanzari_trasabilitate"}
                onClick={() => setActiveReport("vanzari_trasabilitate")}
                className="py-3 px-4 border-0"
              >
                Vanzari in perioada - cu trasabilitate
              </ListGroup.Item>
              <ListGroup.Item
                action
                active={activeReport === "vanzari_produse_finite_reteta"}
                onClick={() => setActiveReport("vanzari_produse_finite_reteta")}
                className="py-3 px-4 border-0"
              >
                Vanzari produse finite - cu scaderi reteta
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>

        {/* Right content column */}
        <Col xs={12} md={9}>
          {activeReport === "tva_incasare" && <JurnalTvaIesiriIncasare />}
          {activeReport === "tva_valuta" && <JurnalTvaIesiriValuta />}

          {activeReport !== "tva_incasare" && activeReport !== "tva_valuta" && (
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="fw-bold mb-0 text-dark">
                    {activeReport === "clienti_neincasati" && "Raport Clienți Neîncasați la Dată"}
                    {activeReport === "iesiri_perioada" && "Raport Ieșiri în Perioadă"}
                    {activeReport === "tva_direct" && "Jurnal TVA Vânzări Direct Taxabile"}
                    {activeReport === "vanzari_trasabilitate" && "Raport Vanzari in Perioada - Cu Trasabilitate"}
                    {activeReport === "vanzari_produse_finite_reteta" && "Raport Vanzari Produse Finite in Perioada - Cu Scaderi Reteta"}
                  </h4>
                  {loaded && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-print-none fw-semibold shadow-sm"
                      onClick={() => window.print()}
                    >
                      <i className="bi bi-printer me-1"></i> Tipărește
                    </Button>
                  )}
                </div>

                {/* Filters */}
                <Form className="bg-light p-3 rounded-4 mb-4 border border-light-subtle d-print-none">
                  <Row className="align-items-end g-3">
                    {activeReport === "clienti_neincasati" && (
                      <Col xs={12} md={4}>
                        <Form.Label className="fw-semibold text-secondary small mb-1">
                          Restanțe la data
                        </Form.Label>
                        <DatePicker
                          selected={atDate}
                          onChange={(date) => setAtDate(date)}
                          dateFormat="dd.MM.yyyy"
                          className="form-control border-0 shadow-sm"
                        />
                      </Col>
                    )}

                    {(activeReport === "iesiri_perioada" || activeReport === "tva_direct" || activeReport === "vanzari_trasabilitate" || activeReport === "vanzari_produse_finite_reteta") && (
                      <>
                        <Col xs={12} md={3} className="d-print-none">
                          <Form.Label className="fw-semibold text-secondary small mb-1">
                            De la
                          </Form.Label>
                          <DatePicker
                            selected={from}
                            onChange={(date) => setFrom(date)}
                            dateFormat="dd.MM.yyyy"
                            className="form-control border-0 shadow-sm"
                          />
                        </Col>
                        <Col xs={12} md={3} className="d-print-none">
                          <Form.Label className="fw-semibold text-secondary small mb-1">
                            Până la
                          </Form.Label>
                          <DatePicker
                            selected={to}
                            onChange={(date) => setTo(date)}
                            dateFormat="dd.MM.yyyy"
                            className="form-control border-0 shadow-sm"
                          />
                        </Col>
                      </>
                    )}

                    {(activeReport === "clienti_neincasati" || activeReport === "iesiri_perioada" || activeReport === "vanzari_trasabilitate" || activeReport === "vanzari_produse_finite_reteta") && (
                      <Col xs={12} md={activeReport === "clienti_neincasati" ? 5 : 3}>
                        <Form.Label className="fw-semibold text-secondary small mb-1">
                          Client (opțional)
                        </Form.Label>
                        <AsyncPaginate
                          value={contact}
                          loadOptions={loadContactOptions}
                          onChange={setContact}
                          isClearable
                          placeholder="Caută client..."
                          className="shadow-sm border-0"
                        />
                      </Col>
                    )}

                    {(activeReport === "vanzari_trasabilitate" || activeReport === "vanzari_produse_finite_reteta") && (
                      <Col xs={12} md={3} className="d-print-none">
                        <Form.Label className="fw-semibold text-secondary small mb-1">
                          Produs Finit (opțional)
                        </Form.Label>
                        <AsyncPaginate
                          value={partFilter}
                          loadOptions={loadPartOptions}
                          onChange={setPartFilter}
                          isClearable
                          placeholder="Caută produs..."
                          className="shadow-sm border-0"
                        />
                      </Col>
                    )}

                    <Col xs={12} md={activeReport === "tva_direct" ? 6 : 2}>
                      <Button
                        onClick={loadData}
                        disabled={busy}
                        className="w-100 fw-semibold shadow-sm"
                      >
                        {busy ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Se încarcă...
                          </>
                        ) : (
                          "Generează Raport"
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {err && (
                  <Alert variant="danger" className="rounded-3 shadow-sm mb-4 border-0">
                    {err}
                  </Alert>
                )}

                {/* Report Tables */}
                <div className="table-responsive">
                  {busy ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <div className="mt-2 text-muted small">Se calculează raportul...</div>
                    </div>
                  ) : (
                    <>
                      {loaded && data.rows && data.rows.length > 0 && (
                        <div className="d-flex justify-content-end mb-3 d-print-none">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="fw-semibold px-3 shadow-sm"
                            onClick={() => window.print()}
                          >
                            🖨️ Tipareste Raport / PDF
                          </Button>
                        </div>
                      )}

                      {/* 1. Clienti Neincasati Report */}
                      {activeReport === "clienti_neincasati" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th style={{ width: 40 }} className="text-center"></th>
                              <th>Client</th>
                              <th style={{ width: 140 }} className="text-end">Nr. Facturi</th>
                              <th style={{ width: 160 }} className="text-end">Rest de Încasat (RON)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedClients.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu există facturi restante la data selectată."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              groupedClients.map((client) => {
                                const isExpanded = !!expandedClients[client.contact_id];
                                return (
                                  <React.Fragment key={client.contact_id}>
                                    <tr
                                      onClick={() => toggleClient(client.contact_id)}
                                      style={{ cursor: "pointer" }}
                                      className="table-light fw-semibold"
                                    >
                                      <td className="text-center text-secondary">
                                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                      </td>
                                      <td>{client.contact_name}</td>
                                      <td className="text-end text-secondary font-monospace">
                                        {client.invoices.length}
                                      </td>
                                      <td className="text-end text-primary font-monospace">
                                        {fmt2(client.total_sold)}
                                      </td>
                                    </tr>

                                    {isExpanded && (
                                      <tr>
                                        <td colSpan={4} className="p-0 bg-light">
                                          <div className="p-3 border-start border-primary border-3">
                                            <Table bordered hover size="sm" className="bg-white m-0 text-secondary">
                                              <thead>
                                                <tr className="table-secondary">
                                                  <th>Număr Document</th>
                                                  <th style={{ width: 120 }} className="text-center">Data Doc.</th>
                                                  <th style={{ width: 140 }} className="text-end">Valoare</th>
                                                  <th style={{ width: 140 }} className="text-end">Încasat</th>
                                                  <th style={{ width: 140 }} className="text-end">Rest de Încasat</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {client.invoices.map((inv) => (
                                                  <tr key={inv.invoice_id}>
                                                    <td>
                                                      <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                                        className="p-0 fw-semibold text-decoration-none"
                                                        onClick={() => navigate(`/iesire/${inv.invoice_id}/edit`)}
                                                      >
                                                        {inv.invoice_no}
                                                      </span>
                                                    </td>
                                                    <td className="text-center font-monospace">{inv.invoice_date}</td>
                                                    <td className="text-end font-monospace">{fmt2(inv.bxgvalue)}</td>
                                                    <td className="text-end font-monospace">{fmt2(inv.settled_value)}</td>
                                                    <td className="text-end font-monospace fw-bold text-danger">{fmt2(inv.bxsold)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })
                            )}
                          </tbody>
                          {groupedClients.length > 0 && (
                            <tfoot className="table-secondary">
                              <tr className="fw-bold">
                                <td></td>
                                <td>TOTAL GENERAL</td>
                                <td className="text-end font-monospace">
                                  {data.rows.length}
                                </td>
                                <td className="text-end font-monospace">
                                  {fmt2(groupedClients.reduce((s, x) => s + x.total_sold, 0))}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      )}

                      {/* 2. Iesiri in Perioada Report */}
                      {activeReport === "iesiri_perioada" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th style={{ width: 110 }} className="text-center">Dată Doc.</th>
                              <th style={{ width: 150 }}>Număr Doc.</th>
                              <th>Client</th>
                              <th style={{ width: 140 }} className="text-end">Valoare Factură (RON)</th>
                              <th style={{ width: 140 }} className="text-end">Bază (RON)</th>
                              <th style={{ width: 140 }} className="text-end">TVA (RON)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.rows.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu s-au găsit documente de ieșire în perioada selectată."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              data.rows.map((r) => (
                                <tr key={r.id}>
                                  <td className="text-center font-monospace">{r.bxdoc_date}</td>
                                  <td>
                                    <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                      className="p-0 fw-semibold text-decoration-none"
                                      onClick={() => navigate(`/iesire/${r.id}/edit`)}
                                    >
                                      {r.bxdoc_no}
                                    </span>
                                  </td>
                                  <td>{r.contact_name}</td>
                                  <td className="text-end font-monospace">{fmt2(r.bxgvalue)}</td>
                                  <td className="text-end font-monospace">{fmt2(r.baza_l)}</td>
                                  <td className="text-end font-monospace">{fmt2(r.tva_l)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                          {data.rows.length > 0 && (
                            <tfoot className="table-secondary">
                              <tr className="fw-bold">
                                <td colSpan={3} className="text-end">TOTAL</td>
                                <td className="text-end font-monospace">
                                  {fmt2(data.rows.reduce((s, x) => s + x.bxgvalue, 0))}
                                </td>
                                <td className="text-end font-monospace">
                                  {fmt2(data.rows.reduce((s, x) => s + x.baza_l, 0))}
                                </td>
                                <td className="text-end font-monospace">
                                  {fmt2(data.rows.reduce((s, x) => s + x.tva_l, 0))}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      )}

                      {/* 3. Jurnal TVA Vânzări Direct Taxabile */}
                      {activeReport === "tva_direct" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th style={{ width: 110 }} className="text-center">Data</th>
                              <th style={{ width: 110 }}>Număr</th>
                              <th>Partener</th>
                              <th style={{ width: 130 }}>CUI</th>
                              <th style={{ width: 70 }} className="text-end">Cota</th>
                              <th style={{ width: 130 }} className="text-end">Bază</th>
                              <th style={{ width: 130 }} className="text-end">TVA</th>
                              <th style={{ width: 130 }} className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedTvaDirect.length === 0 ? (
                              <tr>
                                <td colSpan={8} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu există înregistrări pentru perioada selectată."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              groupedTvaDirect.map(({ doc_id, rows }) => {
                                const first = rows[0] || {};
                                const subBaza = rows.reduce((s, r) => s + Number(r.baza || 0), 0);
                                const subTva = rows.reduce((s, r) => s + Number(r.tva || 0), 0);
                                const subTot = rows.reduce((s, r) => s + Number(r.total || 0), 0);

                                const span = rows.length + 1;

                                return (
                                  <React.Fragment key={doc_id}>
                                    {rows.map((r, idx) => (
                                      <tr key={`${doc_id}-${r.cota}-${idx}`}>
                                        {idx === 0 && (
                                          <>
                                            <td rowSpan={span} className="bg-white">{first.doc_date}</td>
                                            <td rowSpan={span} className="bg-white">
                                              <span className="fw-semibold text-primary text-dark-print cursor-pointer" style={{ cursor: "pointer" }}
                                                className="p-0 fw-semibold text-decoration-none"
                                                onClick={() => navigate(`/iesire/${doc_id}/edit`)}
                                              >
                                                {first.doc_no || ""}
                                              </span>
                                            </td>
                                            <td rowSpan={span} className="bg-white">{first.partener || "-"}</td>
                                            <td rowSpan={span} className="bg-white">{first.cui || ""}</td>
                                          </>
                                        )}
                                        <td className="text-end text-secondary fw-semibold">{Number(r.cota ?? 0)}%</td>
                                        <td className="text-end font-monospace">{fmt2(r.baza)}</td>
                                        <td className="text-end font-monospace">{fmt2(r.tva)}</td>
                                        <td className="text-end font-monospace">{fmt2(r.total)}</td>
                                      </tr>
                                    ))}
                                    <tr className="table-light fw-semibold">
                                      <td className="text-end bg-light small py-1">Subtotal</td>
                                      <td className="text-end bg-light small py-1 font-monospace">{fmt2(subBaza)}</td>
                                      <td className="text-end bg-light small py-1 font-monospace">{fmt2(subTva)}</td>
                                      <td className="text-end bg-light small py-1 font-monospace">{fmt2(subTot)}</td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })
                            )}
                          </tbody>
                          {groupedTvaDirect.length > 0 && (
                            <tfoot className="table-secondary">
                              {(data.totals_by_cota || []).map((t) => (
                                <tr key={`tot-${t.cota}`}>
                                  <td colSpan={5} className="text-end fw-semibold">
                                    Total cotă {Number(t.cota ?? 0)}%
                                  </td>
                                  <td className="text-end fw-semibold font-monospace">{fmt2(t.baza)}</td>
                                  <td className="text-end fw-semibold font-monospace">{fmt2(t.tva)}</td>
                                  <td className="text-end fw-semibold font-monospace">{fmt2(t.total)}</td>
                                </tr>
                              ))}
                              <tr className="table-dark text-white fw-bold">
                                <td colSpan={5} className="text-end py-2">TOTAL GENERAL LEI (RON)</td>
                                <td className="text-end py-2 font-monospace">{fmt2(totalsGeneralDirect.baza)}</td>
                                <td className="text-end py-2 font-monospace">{fmt2(totalsGeneralDirect.tva)}</td>
                                <td className="text-end py-2 font-monospace">{fmt2(totalsGeneralDirect.total)}</td>
                              </tr>
                            </tfoot>
                          )}
                        </Table>
                      )}

                      {/* 4. Vanzari cu Trasabilitate */}
                      {activeReport === "vanzari_trasabilitate" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th>Cod</th>
                              <th>Denumire</th>
                              <th style={{ width: 100 }} className="text-end">Cantitate</th>
                              <th style={{ width: 120 }}>Factura</th>
                              <th style={{ width: 110 }} className="text-center">Data Vanzarii</th>
                              <th>Client</th>
                              <th>Furnizor</th>
                              <th style={{ width: 110 }} className="text-center">Data Intrare</th>
                              <th style={{ width: 120 }} className="text-end">Pret Achizitie</th>
                              <th>Lot</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.rows.length === 0 ? (
                              <tr>
                                <td colSpan={10} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu s-au gasit inregistrari in perioada selectata."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              data.rows.map((r, idx) => (
                                <tr key={idx}>
                                  <td className="font-monospace small">{r.cod || "-"}</td>
                                  <td>{r.denumire}</td>
                                  <td className="text-end font-monospace">{Number(r.cantitate || 0).toFixed(4)}</td>
                                  <td>{r.factura || "-"}</td>
                                  <td className="text-center font-monospace">{r.data_vanzarii || "-"}</td>
                                  <td>{r.client || "-"}</td>
                                  <td>{r.furnizor || "-"}</td>
                                  <td className="text-center font-monospace">{r.data_intrare || "-"}</td>
                                  <td className="text-end font-monospace">{r.pret_achizitie != null ? fmt2(r.pret_achizitie) : "-"}</td>
                                  <td className="font-monospace small">{r.lot || "-"}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      )}

                      {/* 5. Vanzari Produse Finite cu Scaderi Reteta */}
                      {activeReport === "vanzari_produse_finite_reteta" && (
                        <Table hover bordered size="sm" className="align-middle rounded-3 overflow-hidden">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th>Cod Ingredient</th>
                              <th>Denumire Ingredient</th>
                              <th style={{ width: 140 }} className="text-end">Cantitate Scazuta</th>
                              <th>Furnizor Ingredient</th>
                              <th style={{ width: 110 }} className="text-center">Data Intrare</th>
                              <th style={{ width: 120 }} className="text-end">Pret Achizitie</th>
                              <th>Lot</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedFinishedProducts.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="text-center text-muted py-5 bg-white">
                                  {loaded
                                    ? "Nu s-au gasit inregistrari in perioada selectata."
                                    : "Alegeți parametrii și apăsați \"Generează Raport\"."}
                                </td>
                              </tr>
                            ) : (
                              groupedFinishedProducts.map((p, pIdx) => (
                                <React.Fragment key={pIdx}>
                                  <tr className="table-secondary fw-bold text-dark">
                                    <td colSpan={7} className="py-2 px-3">
                                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <span>
                                          📄 Factura: <strong>{p.factura}</strong> ({p.data_vanzarii})
                                        </span>
                                        <span>
                                          👤 Client: <strong>{p.client || "-"}</strong>
                                        </span>
                                        <span>
                                          🍪 Produs Finit: <strong>[{p.finit_cod}] {p.finit_denumire}</strong> (Qty: {Number(p.finit_cantitate || 0).toFixed(2)})
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                  {p.ingredients.map((ing, iIdx) => (
                                    <tr key={`${pIdx}-${iIdx}`}>
                                      <td className="font-monospace small ps-3">{ing.cod || "-"}</td>
                                      <td>{ing.denumire}</td>
                                      <td className="text-end font-monospace">{Number(ing.cantitate || 0).toFixed(4)}</td>
                                      <td>{ing.furnizor || "-"}</td>
                                      <td className="text-center font-monospace">{ing.data_intrare || "-"}</td>
                                      <td className="text-end font-monospace">{ing.pret_achizitie != null ? fmt2(ing.pret_achizitie) : "-"}</td>
                                      <td className="font-monospace small">{ing.lot || "-"}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))
                            )}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
